import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent } from "react"
import {
  ArrowLeft,
  LoaderCircle,
  Bell,
  ChevronRight,
  Sparkles,
  X,
} from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
} from "@/components/ui/card"
import {
  InputGroup,
  InputGroupButton,
} from "@/components/ui/input-group"
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@/components/ui/message-scroller"
import { Textarea } from "@/components/ui/textarea"
import { AvatarIcon } from "@/components/widget/avatar-icon"
import { PrimaryGradientIcon } from "@/components/widget/primary-gradient-icon"
import { usePrefersDark } from "@/hooks/use-prefers-dark"
import { api, ApiError } from "@/lib/api"
import type { ChatbotSettings, ChatMessage, WidgetMotion } from "@/lib/types"
import { cn } from "@/lib/utils"
import { buildGradient, CHAT_WIDGET_DESIGN_TOKENS, contrastColor, gradientContrastColor, launcherSurface, midpointColor, pickThemeColors, readableGradientStops, readableTextColor, resolveLauncherGradient, resolvePrimaryGradient, scaleChatRadius, type ThemeColors } from "@/lib/widget-theme"

type WidgetSettings = Pick<ChatbotSettings,
  | "assistantName" | "chatTitle" | "chatSubtitle" | "chatSubtitleColor" | "chatSubtitleColorDark" | "welcomeMessage" | "suggestedQuestions"
  | "primaryColor" | "primaryColorDark" | "primaryGradientEnabled" | "primaryGradientStart" | "primaryGradientEnd" | "primaryGradientStartDark" | "primaryGradientEndDark" | "primaryGradientAngle" | "launcherColor" | "launcherColorDark"
  | "headerColor" | "headerColorDark" | "headerGradientEnabled" | "headerGradientStart" | "headerGradientEnd" | "headerGradientStartDark" | "headerGradientEndDark" | "headerGradientAngle" | "userMessageColor" | "userMessageColorDark" | "userMessageTextColor" | "userMessageTextColorDark"
  | "assistantMessageColor" | "assistantMessageColorDark" | "textColor" | "textColorDark"
  | "chatBackground" | "chatBackgroundDark" | "chatBackgroundGradientEnabled" | "chatBackgroundGradientStart" | "chatBackgroundGradientEnd" | "chatBackgroundGradientStartDark" | "chatBackgroundGradientEndDark" | "chatBackgroundGradientAngle" | "widgetTheme" | "motionStyle" | "position" | "avatar" | "avatarUrl" | "avatarRadius"
  | "avatarColor"
  | "launcherIcon" | "launcherStyle" | "launcherSize" | "launcherRadius" | "widgetRadius"
  | "launcherGradientStart" | "launcherGradientEnd" | "launcherGradientStartDark" | "launcherGradientEndDark" | "launcherGradientAngle"
  | "launcherGlowEnabled" | "launcherGlowColor" | "launcherGlowColorDark"
  | "widgetSize" | "windowWidth" | "windowHeight" | "showBranding" | "showPoweredBy" | "placeholder" | "status"
  | "messageFontSize" | "headerTitleFontWeight" | "messageFontWeight" | "submitStyle" | "submitColorStyle" | "inputRadius" | "inputShadow" | "showAttachIcon" | "quickActions"
  | "showPlusButton" | "showMicIcon" | "showToolsIcon" | "calloutTitle" | "calloutSubtitle" | "calloutEnabled" | "calloutDelaySeconds" | "calloutColor" | "calloutColorDark"
>

interface ChatWidgetProps {
  settings: WidgetSettings
  publicKey?: string
  sessionToken?: string
  preview?: boolean
  defaultOpen?: boolean
  open?: boolean
  contained?: boolean
  launcherOnlyWhenClosed?: boolean
  onOpenChange?: (open: boolean) => void
  hostDark?: boolean
  onBalloonChange?: (visible: boolean, type?: "callout" | "preview" | null, style?: { backgroundColor: string; background: string; textColor: string; borderRadius: number; capsuleRadius: number }) => void
  previewWidth?: number
  previewHeight?: number
  previewCentered?: boolean
}

type PrimaryGradient = ReturnType<typeof resolvePrimaryGradient>

function readablePrimaryGradient(background: string, gradient: PrimaryGradient, minRatio = 3) {
  return gradient.enabled
    ? { ...readableGradientStops(background, gradient.start, gradient.end, minRatio), angle: gradient.angle }
    : undefined
}

function primaryGradientTextCss(background: string, gradient: PrimaryGradient) {
  const isFigmaDefault = gradient.start.toUpperCase() === "#EF6F00"
    && gradient.end.toUpperCase() === "#EF9800"
    && gradient.angle === 25
  const stops = isFigmaDefault
    ? { start: gradient.start, end: gradient.end }
    : readableGradientStops(background, gradient.start, gradient.end)
  return buildGradient(stops.start, stops.end, gradient.angle)
}

function primaryGradientTextStyle(background: string, fallbackColor: string, gradient: PrimaryGradient): CSSProperties {
  return {
    "--widget-primary-gradient-text": primaryGradientTextCss(background, gradient),
    "--widget-primary-gradient-fallback": fallbackColor,
  } as CSSProperties
}

const dimensions = {
  compact: { width: 344, height: 500 },
  standard: { width: 327, height: 604 },
  large: { width: 424, height: 650 },
} as const

const motionExitDuration: Record<WidgetMotion, number> = {
  smooth: 240,
  spring: 240,
  slide: 220,
  pop: 220,
  fade: 220,
  flip: 320,
  zoom: 260,
  snappy: 160,
  none: 0,
}

function getVisitorId() {
  try {
    const existing = globalThis.localStorage?.getItem("bubbledesk:visitor")
    if (existing) return existing
    const created = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
    globalThis.localStorage?.setItem("bubbledesk:visitor", created)
    return created
  } catch {
    return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  }
}

function getSessionKey() {
  try {
    const existing = globalThis.sessionStorage?.getItem("bubbledesk:session")
    if (existing) return existing
    const created = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
    globalThis.sessionStorage?.setItem("bubbledesk:session", created)
    return created
  } catch {
    return Math.random().toString(36).slice(2)
  }
}

function createSessionKey() {
  const created = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  try {
    globalThis.sessionStorage?.setItem("bubbledesk:session", created)
  } catch {
    // A falha de armazenamento não deve interromper o chat.
  }
  return created
}

const visitorId = getVisitorId()

function trackWidgetEvent(publicKey: string | undefined, sessionToken: string | undefined, sessionKey: string, event: "balloon_open" | "conversation_start" | "assistant_error" | "conversation_close" | "outbound_click", conversationId?: string, url?: string) {
  if (!publicKey || !sessionToken || typeof fetch === "undefined") return
  void fetch(`/api/public/chatbots/${encodeURIComponent(publicKey)}/events`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ visitorId, sessionKey, conversationId, event, url, sessionToken }),
  }).catch(() => undefined)
}
function MarkdownMessage({ content, onOutboundClick }: { content: string; onOutboundClick?: (href: string) => void }) {
  return (
    <div className="markdown-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, href, onClick: _onClick, ...props }) => <a {...props} href={href} target="_blank" rel="noreferrer" onClick={() => onOutboundClick?.(typeof href === "string" ? href : "")} />,
          // Model-produced images must not trigger requests to external trackers.
          img: ({ alt }) => alt ? <span>{alt}</span> : null,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

function parseGenesysQueueUpdate(content: string) {
  const normalized = content.replace(/\s+/g, " ").trim()
  const positionMatch = normalized.match(/(?:sua\s+)?posi(?:ç|c)[aã]o\s+na\s+fila\s*(?:(?:é|e)\s*)?:?\s*(.+?)(?=\s*tempo\s+estimado|$)/i)
  const waitMatch = normalized.match(/tempo\s+estimado(?:\s+para\s+atendimento)?\s*(?:é|e)?\s*(?:de)?\s*:?\s*(.+)$/i)
  if (!positionMatch && !waitMatch) return null

  return {
    position: positionMatch?.[1]?.trim().replace(/[.,;]+$/, ""),
    wait: waitMatch?.[1]?.trim().replace(/[.,;]+$/, ""),
  }
}

function extractPreview(content: string, maxLen = 120): string {
  if (!content) return ""
  const cleaned = content
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/[*_~`>#]/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
  if (cleaned.length <= maxLen) return cleaned
  const truncated = cleaned.slice(0, maxLen)
  const lastSpace = truncated.lastIndexOf(" ")
  return ((lastSpace > maxLen * 0.6 ? truncated.slice(0, lastSpace) : truncated) + "...").trim()
}

const INITIAL_WELCOME = (welcomeMessage: string): ChatMessage => ({
  id: "welcome",
  role: "assistant",
  content: welcomeMessage,
})

type WidgetMessage = ChatMessage & { failedReason?: string }

const MENU_COMMANDS = new Set(["menu", "voltar ao menu", "voltar", "inicio"])

function isMenuCommand(content: string) {
  const normalized = content
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
  return MENU_COMMANDS.has(normalized)
}

function friendlyFailureMessage(error: unknown) {
  const offline = "Sem conexão. Não consegui enviar sua mensagem."
  if (error instanceof ApiError) {
    if (error.status === 0) return offline
    if (error.status === 408 || error.status === 504) return "A resposta demorou demais. Tente novamente."
    if (error.status === 429) return "Muitas mensagens em pouco tempo. Aguarde um instante e tente novamente."
    if (error.status >= 500) return "O atendimento está instável no momento. Não consegui enviar sua mensagem."
    return "Não consegui enviar sua mensagem. Tente novamente."
  }
  const errorName = typeof error === "object" && error !== null ? (error as { name?: unknown }).name : undefined
  if (errorName === "AbortError" || errorName === "TimeoutError") {
    return "A resposta demorou demais. Tente novamente."
  }
  if (error instanceof TypeError || globalThis.navigator?.onLine === false) return offline
  return "Não consegui enviar sua mensagem. Tente novamente."
}

type WidgetHandoffState = "requested" | "dispatching" | "recovering" | "active"
type WidgetQueueStatus = { queueName: string; estimatedWaitTimeSeconds?: number }
type StoredWidgetSession = {
  version: 1
  sessionKey: string
  conversationId?: string
  messages: WidgetMessage[]
  handoffState: WidgetHandoffState | null
  queueStatus: WidgetQueueStatus | null
  eventCursor?: string
  open: boolean
}

function widgetSessionStorageKey(publicKey: string | undefined, sessionKey: string) {
  return `bubbledesk:widget:${publicKey ?? "preview"}:${sessionKey}`
}

function readWidgetSession(publicKey: string | undefined, sessionKey: string): StoredWidgetSession | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(widgetSessionStorageKey(publicKey, sessionKey))
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<StoredWidgetSession>
    if (value.version !== 1 || value.sessionKey !== sessionKey || !Array.isArray(value.messages)) return null
    const messages = value.messages.filter((message): message is WidgetMessage =>
      typeof message === "object" && message !== null &&
      typeof message.id === "string" && (message.role === "user" || message.role === "assistant") &&
      typeof message.content === "string" &&
      (message.failedReason === undefined || typeof message.failedReason === "string"),
    ).slice(-100)
    const handoffState = ["requested", "dispatching", "recovering", "active"].includes(value.handoffState ?? "")
      ? value.handoffState as WidgetHandoffState
      : null
    const queueStatus = typeof value.queueStatus?.queueName === "string"
      ? {
          queueName: value.queueStatus.queueName,
          ...(typeof value.queueStatus.estimatedWaitTimeSeconds === "number"
            ? { estimatedWaitTimeSeconds: value.queueStatus.estimatedWaitTimeSeconds }
            : {}),
        }
      : null
    return {
      version: 1,
      sessionKey,
      ...(typeof value.conversationId === "string" ? { conversationId: value.conversationId } : {}),
      messages,
      handoffState,
      queueStatus,
      ...(typeof value.eventCursor === "string" && /^\d{1,19}$/.test(value.eventCursor) ? { eventCursor: value.eventCursor } : {}),
      open: value.open === true,
    }
  } catch {
    return null
  }
}

function bootWidgetSession(publicKey: string | undefined) {
  const sessionKey = getSessionKey()
  const stored = readWidgetSession(publicKey, sessionKey)
  // Recarregar a página com o chat fechado volta ao menu inicial, exceto durante um atendimento humano.
  if (stored && !stored.open && !stored.handoffState && stored.messages.some((message) => message.id !== "welcome")) {
    try {
      globalThis.sessionStorage?.removeItem(widgetSessionStorageKey(publicKey, sessionKey))
    } catch {
      // A falha de armazenamento não deve interromper o chat.
    }
    return { sessionKey: createSessionKey(), restored: null, fresh: true }
  }
  return { sessionKey, restored: stored, fresh: false }
}

export function ChatWidget({
  settings,
  publicKey,
  sessionToken,
  preview = false,
  defaultOpen = false,
  open: controlledOpen,
  contained = false,
  launcherOnlyWhenClosed = false,
  onOpenChange,
  onBalloonChange,
  hostDark,
  previewWidth,
  previewHeight,
  previewCentered = false,
}: ChatWidgetProps) {
  const [boot] = useState(() => bootWidgetSession(publicKey))
  const [sessionKey, setSessionKey] = useState(boot.sessionKey)
  const [restoredSession] = useState(boot.restored)
  const freshSessionRef = useRef(boot.fresh)
  const [internalOpen, setInternalOpen] = useState(() => Boolean(restoredSession?.open || restoredSession?.handoffState) || defaultOpen)
  const open = controlledOpen ?? internalOpen
  const motionStyle = settings.motionStyle ?? "smooth"
  const [renderOpen, setRenderOpen] = useState(Boolean(restoredSession?.open || restoredSession?.handoffState) || defaultOpen || controlledOpen === true)
  const prefersDark = usePrefersDark()
  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  const [conversationId, setConversationId] = useState<string | undefined>(restoredSession?.conversationId)
  const [messages, setMessages] = useState<WidgetMessage[]>(() => restoredSession?.messages.length ? restoredSession.messages : [INITIAL_WELCOME(settings.welcomeMessage)])
  const [genesysHandoffState, setGenesysHandoffState] = useState<WidgetHandoffState | null>(restoredSession?.handoffState ?? null)
  const [genesysQueueStatus, setGenesysQueueStatus] = useState<WidgetQueueStatus | null>(restoredSession?.queueStatus ?? null)
  const [restoringSession, setRestoringSession] = useState(!restoredSession && !boot.fresh && !preview && Boolean(publicKey && sessionToken))
  const handoffStatusMessageIdRef = useRef<string | null>(null)
  const conversationEventCursorRef = useRef<{ conversationId: string; sequence?: string } | null>(
    restoredSession?.conversationId
      ? { conversationId: restoredSession.conversationId, sequence: restoredSession.eventCursor ?? "0" }
      : null,
  )
  const [chatbotPausedByEvent, setChatbotPausedByEvent] = useState(false)
  const [pendingPreview, setPendingPreview] = useState<string | null>(null)
  const [calloutVisible, setCalloutVisible] = useState(false)
  const [calloutDismissed, setCalloutDismissed] = useState(false)
  const openRef = useRef(false)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeAnimationRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (restoredSession || freshSessionRef.current || preview || !publicKey || !sessionToken) {
      setRestoringSession(false)
      return
    }
    const controller = new AbortController()
    setRestoringSession(true)
    void api.restoreWidgetConversation(publicKey, {
      sessionToken, visitorId, sessionKey, signal: controller.signal,
    }).then((result) => {
      if (controller.signal.aborted || !result.conversation) return
      const restored = result.conversation
      setConversationId(restored.conversationId)
      setMessages(restored.messages.length ? restored.messages : [INITIAL_WELCOME(settings.welcomeMessage)])
      setGenesysHandoffState(restored.handoffState)
      setGenesysQueueStatus(null)
      conversationEventCursorRef.current = { conversationId: restored.conversationId, sequence: restored.eventCursor ?? "0" }
      if (restored.statusMessage) {
        setMessages((current) => current.some((message) => message.id === restored.statusMessage!.id)
          ? current
          : [...current, restored.statusMessage!])
      }
      setInternalOpen(true)
      setRenderOpen(true)
    }).catch(() => undefined).finally(() => {
      if (!controller.signal.aborted) setRestoringSession(false)
    })
    return () => controller.abort()
  }, [preview, publicKey, restoredSession, sessionKey, sessionToken, settings.welcomeMessage])

  useEffect(() => {
    try {
      const currentEventCursor = conversationEventCursorRef.current
      const snapshot: StoredWidgetSession = {
        version: 1,
        sessionKey,
        ...(conversationId ? { conversationId } : {}),
        messages: messages.slice(-100),
        handoffState: genesysHandoffState,
        queueStatus: genesysQueueStatus,
        ...(currentEventCursor && currentEventCursor.conversationId === conversationId && currentEventCursor.sequence
          ? { eventCursor: currentEventCursor.sequence }
          : {}),
        open,
      }
      globalThis.sessionStorage?.setItem(widgetSessionStorageKey(publicKey, sessionKey), JSON.stringify(snapshot))
    } catch {
      // A falha de armazenamento não deve interromper o chat.
    }
  }, [conversationId, genesysHandoffState, genesysQueueStatus, messages, open, publicKey, sessionKey])

  useEffect(() => {
    if ((!open && !genesysHandoffState) || !conversationId || !publicKey || !sessionToken || preview) return
    const controller = new AbortController()
    if (conversationEventCursorRef.current?.conversationId !== conversationId) {
      conversationEventCursorRef.current = { conversationId }
    }
    let cursor = conversationEventCursorRef.current.sequence
    const reconnectDelay = () => new Promise((resolve) => setTimeout(resolve, 1_000))
    void (async () => {
      while (!controller.signal.aborted) {
        try {
          for await (const event of api.streamConversationEvents(publicKey, conversationId, {
            sessionToken, visitorId, sessionKey, after: cursor, signal: controller.signal,
          })) {
            if (controller.signal.aborted) break
            cursor = event.sequence
            if (conversationEventCursorRef.current?.conversationId === conversationId) {
              conversationEventCursorRef.current.sequence = event.sequence
            }
            if (event.type === "handoff_state") {
              const liveState = ["requested", "dispatching", "recovering", "active"].includes(event.state)
                ? event.state as "requested" | "dispatching" | "recovering" | "active"
                : null
              setGenesysHandoffState(liveState)
              if (liveState !== "recovering") setGenesysQueueStatus(null)
              if (typeof event.chatbotPaused === "boolean") setChatbotPausedByEvent(event.chatbotPaused)
              if (event.content) {
                if (event.state === "requested" && event.messageId) handoffStatusMessageIdRef.current = event.messageId
                const messageId = event.state === "reason_requested"
                  ? event.messageId ?? `genesys-handoff-reason-${conversationId}`
                  : event.messageId ?? handoffStatusMessageIdRef.current ?? `genesys-handoff-status-${conversationId}`
                const content = event.content
                setMessages((current) => current.some((message) => message.id === messageId)
                  ? current.map((message) => message.id === messageId ? { ...message, content } : message)
                  : [...current, { id: messageId, role: "assistant", content }])
              }
            } else if (event.type === "agent_message") {
              setMessages((current) => current.some((message) => message.id === event.messageId)
                ? current
                : [...current, { id: event.messageId, role: "assistant", authorType: "genesys_agent", content: event.content }])
            } else if (event.type === "queue_message") {
              setMessages((current) => current.some((message) => message.id === event.messageId)
                ? current
                : [...current, { id: event.messageId, role: "assistant", authorType: "genesys_automation", content: event.content }])
            } else if (event.type === "queue_status") {
              setGenesysQueueStatus(event.queueName
                ? {
                    queueName: event.queueName,
                    ...(typeof event.estimatedWaitTimeSeconds === "number" ? { estimatedWaitTimeSeconds: event.estimatedWaitTimeSeconds } : {}),
                  }
                : null)
            }
          }
        } catch (error) {
          if (controller.signal.aborted) break
          if (error instanceof Error && "status" in error && (error as { status?: number }).status === 403) {
            setGenesysHandoffState(null)
            break
          }
        }
        if (!controller.signal.aborted) await reconnectDelay()
      }
    })()
    return () => controller.abort()
  }, [conversationId, genesysHandoffState, open, preview, publicKey, sessionKey, sessionToken])

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    if (!input) {
      el.style.height = ""
      return
    }
    el.style.height = "auto"
    el.style.height = `${Math.max(40, Math.min(el.scrollHeight, 120))}px`
  }, [input])

  useEffect(() => {
    setMessages((current) =>
      current.length === 1 && current[0].id === "welcome"
        ? [INITIAL_WELCOME(settings.welcomeMessage)]
        : current,
    )
  }, [settings.welcomeMessage])

  useEffect(() => {
    if (closeAnimationRef.current) {
      clearTimeout(closeAnimationRef.current)
      closeAnimationRef.current = null
    }
    if (open) {
      setRenderOpen(true)
      return
    }
    if (!renderOpen) return
    const duration = motionExitDuration[motionStyle]
    if (duration === 0) {
      setRenderOpen(false)
      return
    }
    closeAnimationRef.current = setTimeout(() => {
      setRenderOpen(false)
      closeAnimationRef.current = null
    }, duration)
    return () => {
      if (closeAnimationRef.current) {
        clearTimeout(closeAnimationRef.current)
        closeAnimationRef.current = null
      }
    }
  }, [motionStyle, open, renderOpen])

  useEffect(() => { openRef.current = open }, [open])

  useEffect(() => {
    if (open && pendingPreview) setPendingPreview(null)
    if (open && dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null }
  }, [open, pendingPreview])

  useEffect(() => {
    if (!pendingPreview) {
      if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null }
      return
    }
    dismissTimerRef.current = setTimeout(() => setPendingPreview(null), 15_000)
    return () => { if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null } }
  }, [pendingPreview])

  const calloutTitle = settings.calloutTitle?.trim() ?? ""
  const calloutSubtitle = settings.calloutSubtitle?.trim() ?? ""
  const calloutReady = Boolean(settings.calloutEnabled && (calloutTitle || calloutSubtitle))

  useEffect(() => {
    if (!calloutReady || open || pendingPreview) {
      setCalloutVisible(false)
      return
    }
    if (calloutDismissed) {
      const retry = setTimeout(() => { setCalloutDismissed(false) }, 8000)
      return () => clearTimeout(retry)
    }
    const delay = Math.min(60, Math.max(0, settings.calloutDelaySeconds ?? 3)) * 1000
    const timer = setTimeout(() => setCalloutVisible(true), delay)
    return () => clearTimeout(timer)
  }, [calloutReady, open, calloutDismissed, pendingPreview, settings.calloutDelaySeconds])

  useEffect(() => {
    if ((calloutVisible || pendingPreview !== null) && !preview && !launcherOnlyWhenClosed) {
      trackWidgetEvent(publicKey, sessionToken, sessionKey, "balloon_open")
    }
  }, [calloutVisible, launcherOnlyWhenClosed, pendingPreview, preview, publicKey, sessionKey, sessionToken])

  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next)
    else onOpenChange?.(next)
  }
  useEffect(() => {
    if (!launcherOnlyWhenClosed) return
    const onParentMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || event.origin !== window.location.origin) return
      if (event.data?.type === "bubbledesk:open") setOpen(true)
      if (event.data?.type === "bubbledesk:dismiss-callout") {
        setCalloutVisible(false)
        setCalloutDismissed(true)
      }
    }
    window.addEventListener("message", onParentMessage)
    return () => window.removeEventListener("message", onParentMessage)
  }, [launcherOnlyWhenClosed, setOpen])
  useEffect(() => {
    if (controlledOpen === undefined) onOpenChange?.(open)
  }, [controlledOpen, onOpenChange, open])

  function returnToMenu() {
    if (sending || genesysHandoffState) return
    setInput("")
    if (showEmptyState) return
    try {
      globalThis.sessionStorage?.removeItem(widgetSessionStorageKey(publicKey, sessionKey))
    } catch {
      // A falha de armazenamento não deve interromper o chat.
    }
    freshSessionRef.current = true
    conversationEventCursorRef.current = null
    handoffStatusMessageIdRef.current = null
    setSessionKey(createSessionKey())
    setConversationId(undefined)
    setGenesysQueueStatus(null)
    setChatbotPausedByEvent(false)
    setMessages([INITIAL_WELCOME(settings.welcomeMessage)])
  }

  async function submitMessage(content: string, retryMessageId?: string) {
    if (!content || sending || (!preview && settings.status !== "active") || chatbotPausedByEvent) return
    if (!retryMessageId && !genesysHandoffState && isMenuCommand(content)) {
      returnToMenu()
      return
    }
    setInput("")

    const userMessage: WidgetMessage = { id: retryMessageId ?? crypto.randomUUID(), role: "user", content }
    const placeholderId = crypto.randomUUID()
    const placeholderMessage: ChatMessage = { id: placeholderId, role: "assistant", content: "" }
    setMessages((current) => retryMessageId
      ? [...current.map((m) => m.id === retryMessageId ? { id: m.id, role: m.role, content: m.content } : m), placeholderMessage]
      : [...current, userMessage, placeholderMessage])

    setSending(true)
    let assistantId: string = placeholderId
    const updateAssistant = (mutator: (msg: ChatMessage) => ChatMessage) => {
      setMessages((current) => current.map((m) => (m.id === assistantId ? mutator(m) : m)))
    }
    const replaceAssistant = (next: ChatMessage) => {
      setMessages((current) => current.map((m) => (m.id === placeholderId ? next : m)))
      assistantId = next.id
    }

    try {
      if (preview || !publicKey) {
        const canned = "Encontrei isso nas páginas autorizadas: seus visitantes recebem respostas objetivas, com as fontes certas e a identidade da sua marca."
        const chunkSize = 3
        const delayMs = 14
        for (let i = 0; i < canned.length; i += chunkSize) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
          updateAssistant((m) => ({ ...m, content: m.content + canned.slice(i, i + chunkSize) }))
        }
      } else {
        for await (const event of api.streamMessage(publicKey, {
          message: content,
          conversationId,
          visitorId,
          sessionKey,
          pageUrl: document.referrer || undefined,
          sessionToken,
        })) {
          if (event.type === "start") {
            setConversationId(event.conversationId)
            replaceAssistant({ id: event.messageId, role: "assistant", content: "", sources: event.sources })
          } else if (event.type === "delta") {
            updateAssistant((m) => ({ ...m, content: m.content + event.content }))
          } else if (event.type === "handoff_state") {
            const liveState = ["requested", "dispatching", "recovering", "active"].includes(event.state)
              ? event.state as "requested" | "dispatching" | "recovering" | "active"
              : null
            setGenesysHandoffState(liveState)
            if (event.content) {
              if (event.state === "requested" && event.messageId) handoffStatusMessageIdRef.current = event.messageId
              const messageId = event.state === "reason_requested"
                ? event.messageId ?? `genesys-handoff-reason-${conversationId ?? "new"}`
                : handoffStatusMessageIdRef.current ?? event.messageId ?? `genesys-handoff-status-${conversationId ?? "new"}`
              updateAssistant((m) => ({ ...m, id: messageId, content: event.content! }))
            } else {
              setMessages((current) => current.filter((message) => message.id !== placeholderId))
            }
          } else if (event.type === "error") {
            updateAssistant((m) => ({ ...m, content: event.message || "Tive um problema para responder. Tente novamente." }))
            trackWidgetEvent(publicKey, sessionToken, sessionKey, "assistant_error", conversationId)
          }
        }
      }
    } catch (error) {
      const failedReason = friendlyFailureMessage(error)
      setMessages((current) => current
        .filter((m) => m.id !== placeholderId && m.id !== assistantId)
        .map((m) => m.id === userMessage.id ? { ...m, failedReason } : m))
    } finally {
      setSending(false)
      if (!openRef.current) {
        setMessages((current) => {
          for (let i = current.length - 1; i >= 0; i -= 1) {
            const m = current[i]
            if (m.role === "assistant" && m.content && m.content.length > 12) {
              const preview = extractPreview(m.content)
              if (preview) setPendingPreview(preview)
              break
            }
          }
          return current
        })
      }
    }
  }

  function send(event: FormEvent) {
    event.preventDefault()
    void submitMessage(input.trim())
  }

  const showEmptyState = messages.length === 1 && messages[0].id === "welcome"
  const isCompact = settings.widgetSize === "compact"
  const size = {
    width: previewWidth ?? settings.windowWidth ?? dimensions[settings.widgetSize].width,
    height: previewHeight ?? settings.windowHeight ?? dimensions[settings.widgetSize].height,
  }
  const isFigmaDefaultGeometry = size.width === 327 && size.height === 604 && settings.widgetRadius === 30
  const shellDark = settings.widgetTheme === "dark" ? true : settings.widgetTheme === "light" ? false : hostDark ?? prefersDark
  const theme: ThemeColors = pickThemeColors(settings, shellDark)
  const primaryGradient = resolvePrimaryGradient(settings, shellDark)
  const primaryGradientCss = buildGradient(primaryGradient.start, primaryGradient.end, primaryGradient.angle)
  const isFigmaDefaultPrimaryGradient = primaryGradient.enabled
    && primaryGradient.start.toUpperCase() === "#EF6F00"
    && primaryGradient.end.toUpperCase() === "#EF9800"
    && primaryGradient.angle === 25
  const primaryColor = primaryGradient.enabled
    ? readableTextColor(theme.chatBackground, primaryGradient.solid)
    : theme.primaryColor
  // As bolhas laranja do Figma usam texto branco; temas/gradientes customizados mantêm o auto-contraste.
  const primaryGradientText = isFigmaDefaultPrimaryGradient
    ? "#FFFFFF"
    : gradientContrastColor(primaryGradient.start, primaryGradient.end)
  // O gradiente acompanha todos os acentos vinculados à cor primária; as superfícies
  // de cabeçalho e launcher continuam obedecendo às configurações próprias.
  const headerGradientEnabled = settings.headerGradientEnabled ?? false
  // Semântica: chatTitle = título do cabeçalho (UI), assistantName = identidade da IA (system prompt).
  // Header usa chatTitle com fallback para assistantName — nenhum campo morto.
  const headerTitle = settings.chatTitle.trim() || settings.assistantName.trim() || "Assistente"
  const textColor = theme.textColor
  const assistantBg = theme.assistantMessageColor
  const automaticUserText = primaryGradient.enabled ? primaryGradientText : contrastColor(theme.userMessageColor)
  const configuredUserText = shellDark ? settings.userMessageTextColorDark : settings.userMessageTextColor
  const userText = configuredUserText || automaticUserText
  const headerGradientStart = shellDark ? settings.headerGradientStartDark : settings.headerGradientStart
  const headerGradientEnd = shellDark ? settings.headerGradientEndDark : settings.headerGradientEnd
  const resolvedHeaderGradientStart = headerGradientStart ?? theme.headerColor
  const resolvedHeaderGradientEnd = headerGradientEnd ?? (primaryGradient.enabled ? primaryGradient.end : theme.primaryColor)
  const headerBackground = headerGradientEnabled
    ? midpointColor(resolvedHeaderGradientStart, resolvedHeaderGradientEnd)
    : theme.headerColor
  const headerText = readableTextColor(headerBackground, primaryColor)
  const headerTitleStyle: CSSProperties = {
    ...(primaryGradient.enabled ? primaryGradientTextStyle(headerBackground, headerText, primaryGradient) : { color: headerText }),
    fontWeight: settings.headerTitleFontWeight ?? 700,
  }
  const headerIconGradient = readablePrimaryGradient(headerBackground, primaryGradient)
  const previewPrimaryTextStyle = primaryGradient.enabled ? primaryGradientTextStyle(assistantBg, primaryColor, primaryGradient) : { color: primaryColor }
  const placeholderGradientCss = primaryGradient.enabled && !isFigmaDefaultPrimaryGradient
    ? primaryGradientTextCss(theme.chatBackground, primaryGradient)
    : undefined
  const primaryIconGradient = isFigmaDefaultPrimaryGradient
    ? { start: primaryGradient.start, end: primaryGradient.end, angle: primaryGradient.angle }
    : readablePrimaryGradient(theme.chatBackground, primaryGradient)
  const primaryIconGradientOnAssistant = readablePrimaryGradient(assistantBg, primaryGradient)
  const primaryIconGradientOnSuggestion = readablePrimaryGradient("#F9F9F9", primaryGradient)
  const primaryIconGradientCss = primaryIconGradient
    ? buildGradient(primaryIconGradient.start, primaryIconGradient.end, primaryIconGradient.angle)
    : primaryGradientCss
  const headerActionText = readableTextColor(headerBackground, theme.textColor)
  const selectedSubtitleColor = shellDark ? settings.chatSubtitleColorDark : settings.chatSubtitleColor
  const subtitleColor = selectedSubtitleColor || "#3ABF38"
  const scrollThumb = `color-mix(in srgb, ${primaryColor} 38%, ${textColor} 14%)`
  const scrollThumbHover = `color-mix(in srgb, ${primaryColor} 55%, ${textColor} 22%)`
  const positionIsLeft = settings.position.endsWith("-left")
  const positionIsTop = settings.position.startsWith("top-")
  // O iframe externo muda de tamanho ao abrir e fechar, mas sua borda inferior e
  // lateral fica fixa. Mantenha o launcher sempre ancorado no mesmo ponto interno.
  const expandedLayout = open || renderOpen
  const align = contained
    ? (positionIsLeft ? "items-start" : "items-end")
    : (positionIsLeft
      ? "left-5 items-start"
      : "right-5 items-end")
  const edge = contained
    ? (positionIsTop ? "top-0" : "bottom-0")
    : (positionIsTop
      ? "top-3"
      : "bottom-5")
  const messageFontSize = settings.messageFontSize ?? 14
  const welcomeHeadingFontSize = Math.round((messageFontSize * 16) / 14)
  const welcomeHelperFontSize = Math.max(12, Math.round((messageFontSize * 12) / 14))
  const messageFontWeight = settings.messageFontWeight ?? 400
  // Um único controle escala os raios relativos do Figma em todas as peças.
  const radius = (base: number) => scaleChatRadius(base, settings.widgetRadius)
  const shellRadius = isFigmaDefaultGeometry ? 29.76 : radius(CHAT_WIDGET_DESIGN_TOKENS.radius.shell)
  const suggestionRadius = isFigmaDefaultGeometry ? 11.904 : radius(CHAT_WIDGET_DESIGN_TOKENS.radius.suggestion)
  const configuredInputRadius = settings.inputRadius ?? CHAT_WIDGET_DESIGN_TOKENS.radius.input
  const inputRadius = isFigmaDefaultGeometry && configuredInputRadius === 22 ? 22.32 : radius(configuredInputRadius)
  const avatarColor = settings.avatarColor || primaryColor
  const avatarGradient = settings.avatarColor ? undefined : readablePrimaryGradient(theme.chatBackground, primaryGradient)
  const avatarTintBackground = avatarGradient
    ? `linear-gradient(${avatarGradient.angle}deg, color-mix(in srgb, ${avatarGradient.start} 15%, transparent), color-mix(in srgb, ${avatarGradient.end} 15%, transparent))`
    : `color-mix(in srgb, ${avatarColor} 15%, transparent)`
  const avatarFrameStyle: CSSProperties = isFigmaDefaultPrimaryGradient
    ? { background: "linear-gradient(29.52deg, #FD8627 39.494%, #FFCD01 84.795%, #307241 106.93%)", padding: 1.5 }
    : { backgroundColor: "rgba(255,255,255,.15)", padding: 0 }
  const avatarRadius = settings.avatarRadius >= 999 ? 50 : Math.min(50, Math.max(0, settings.avatarRadius / 2))
  const inputShadow = settings.inputShadow === "strong"
    ? "0 6px 18px -8px rgba(24, 20, 45, .42)"
    : settings.inputShadow === "subtle"
      ? "3.72px 2.976px 7.44px rgba(0, 0, 0, .13)"
      : "none"
  const submitRadius = radius(CHAT_WIDGET_DESIGN_TOKENS.radius.send)
  const submitStyle: CSSProperties = settings.submitColorStyle === "neutral"
    ? { backgroundColor: "transparent", color: textColor, border: "0", borderRadius: submitRadius, opacity: 1 }
    : { backgroundColor: "transparent", color: primaryColor, border: "0", borderRadius: submitRadius, opacity: 1 }

  const launcherGradient = resolveLauncherGradient({
    launcherColor: theme.launcherColor,
    primaryColor: primaryGradient.enabled ? primaryGradient.end : theme.primaryColor,
    gradientStart: shellDark ? settings.launcherGradientStartDark ?? settings.launcherGradientStart : settings.launcherGradientStart,
    gradientEnd: shellDark ? settings.launcherGradientEndDark ?? settings.launcherGradientEnd : settings.launcherGradientEnd,
    gradientAngle: settings.launcherGradientAngle,
  })
  const configuredLauncherStyle = launcherSurface(settings.launcherStyle, {
    launcherColor: theme.launcherColor,
    primaryColor: primaryGradient.enabled ? primaryGradient.end : theme.primaryColor,
    assistantMessageColor: assistantBg,
    gradientStart: launcherGradient.start,
    gradientEnd: launcherGradient.end,
    gradientAngle: launcherGradient.angle,
  })
  const launcherStyle = configuredLauncherStyle
  const launcherGlowColor = shellDark ? settings.launcherGlowColorDark ?? settings.launcherGlowColor : settings.launcherGlowColor
  const isFigmaDefaultLauncher = isFigmaDefaultGeometry
    && settings.launcherStyle === "gradient"
    && launcherGradient.start.toUpperCase() === "#EF6F00"
    && launcherGradient.end.toUpperCase() === "#EF9800"
    && launcherGradient.angle === 79
    && launcherGlowColor?.toUpperCase() === "#FFE000"
  const launcherGlow = settings.launcherGlowEnabled !== false && launcherGlowColor
    ? `0 4px ${isFigmaDefaultLauncher ? 28.05 : 56}px color-mix(in srgb, ${launcherGlowColor} 40%, transparent)`
    : "none"
  const renderedLauncherStyle = { ...launcherStyle, boxShadow: launcherGlow }
  const headerStyle: CSSProperties = {
    ...(headerGradientEnabled
      ? { background: buildGradient(resolvedHeaderGradientStart, resolvedHeaderGradientEnd, settings.headerGradientAngle ?? 135, true) }
      : { backgroundColor: theme.headerColor }),
    color: headerText,
    height: 70,
    minHeight: 70,
    boxSizing: "border-box",
    padding: isFigmaDefaultGeometry ? "15px 12.812px 9px" : "15px 13px 9px",
    gap: isFigmaDefaultGeometry ? 31 : 14.88,
    justifyContent: isFigmaDefaultGeometry ? "center" : undefined,
    borderBottom: `${isFigmaDefaultGeometry ? 0.744 : 1}px solid #E6E6E6`,
  }
  const widgetCardStyle: CSSProperties & Record<"--scroll-thumb" | "--scroll-thumb-hover", string> = {
    width: size.width,
    height: size.height,
    maxWidth: contained ? "100%" : "calc(100vw - 22px)",
    maxHeight: contained ? "100%" : "calc(100vh - 26px)",
    borderRadius: shellRadius,
    backgroundColor: theme.chatBackground,
    boxShadow: "0 2px 12px rgba(37, 37, 37, 0.10)",
    color: textColor,
    fontFamily: CHAT_WIDGET_DESIGN_TOKENS.fontFamily,
    "--scroll-thumb": scrollThumb,
    "--scroll-thumb-hover": scrollThumbHover,
  }
  const motionDirectionClass = positionIsLeft ? "widget-motion-from-left" : "widget-motion-from-right"
  const cardMotionClass = cn(`widget-card-motion-${motionStyle}-${open ? "enter" : "exit"}`, motionStyle === "slide" && motionDirectionClass)
  const launcherMotionClass = cn(`widget-launcher-motion-${motionStyle}`, motionStyle === "slide" && motionDirectionClass)
  const launcherCustom = /^(data:image\/|https?:\/\/)/i.test(settings.launcherIcon)
  const calloutHex = (shellDark ? settings.calloutColorDark : settings.calloutColor)?.trim() ?? ""
  const calloutSolid = /^#[0-9a-f]{6}$/i.test(calloutHex) ? calloutHex : ""
  const calloutGradientStart = launcherGradient.start
  const calloutGradientEnd = launcherGradient.end
  const calloutBackground = calloutSolid
    ? calloutSolid
    : buildGradient(launcherGradient.start, launcherGradient.end, launcherGradient.angle, true)
  const calloutText = calloutSolid ? contrastColor(calloutSolid) : gradientContrastColor(calloutGradientStart, calloutGradientEnd)
  const calloutCornerRadius = radius(CHAT_WIDGET_DESIGN_TOKENS.radius.callout)
  const calloutCapsuleRadius = radius(CHAT_WIDGET_DESIGN_TOKENS.radius.shell)
  // Só o canto que "aponta" pro launcher fica quase reto; os outros três ficam em cápsula.
  const calloutTL = !positionIsTop && positionIsLeft ? calloutCornerRadius : calloutCapsuleRadius
  const calloutTR = !positionIsTop && !positionIsLeft ? calloutCornerRadius : calloutCapsuleRadius
  const calloutBR = positionIsTop && !positionIsLeft ? calloutCornerRadius : calloutCapsuleRadius
  const calloutBL = positionIsTop && positionIsLeft ? calloutCornerRadius : calloutCapsuleRadius
  const calloutBorderRadius = `${calloutTL}px ${calloutTR}px ${calloutBR}px ${calloutBL}px`
  const calloutStyle: CSSProperties = {
    borderRadius: calloutBorderRadius,
    ...(calloutSolid ? { backgroundColor: calloutSolid } : { background: calloutBackground }),
    color: calloutText,
    borderColor: "transparent",
  }
  const calloutBoxStyle: CSSProperties = {
    ...calloutStyle,
    position: "absolute",
    width: "max-content",
    minHeight: 36,
    maxWidth: "min(420px, calc(100vw - 116px))",
    ...(positionIsLeft
      ? { left: settings.launcherSize + 16 }
      : { right: settings.launcherSize + 16 }),
    ...(positionIsTop ? { top: 0 } : { bottom: 0 }),
    borderRadius: calloutStyle.borderRadius,
  }

  useEffect(() => {
    const visible = calloutVisible || pendingPreview !== null
    const type = pendingPreview !== null ? "preview" : calloutVisible ? "callout" : null
    const solidColor = calloutSolid || calloutGradientStart
    onBalloonChange?.(visible, type, { backgroundColor: solidColor, background: calloutBackground, textColor: calloutText, borderRadius: calloutCornerRadius, capsuleRadius: calloutCapsuleRadius })
  }, [calloutBackground, calloutGradientStart, calloutSolid, calloutText, calloutVisible, launcherOnlyWhenClosed, pendingPreview, onBalloonChange])

  const unavailable = (!preview && settings.status !== "active") || chatbotPausedByEvent
  const canSend = !!input.trim() && !sending && !unavailable
  const lastMessage = messages.at(-1)
  const showThinkingDots = sending && lastMessage?.role === "assistant" && !lastMessage.content.trim()

  return (
    <div className={cn("chat-widget", contained ? "absolute inset-0 z-10 flex flex-col justify-end p-3" : "fixed z-50 flex flex-col", previewCentered ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : cn(edge, align))}>
      {!launcherOnlyWhenClosed && !open && !pendingPreview && calloutVisible ? (
        <div
          role="status"
          aria-live="polite"
          className="chat-preview-bubble absolute flex min-h-9 w-max items-center border px-4 py-1.5 shadow-[0_4px_14px_-4px_rgba(35,28,74,0.2)] max-w-[min(420px,calc(100vw-116px))]"
          style={calloutBoxStyle}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="min-w-0 flex-1 text-left"
          >
            {calloutTitle ? <p className="truncate text-[16px] font-bold leading-[22px]">{calloutTitle}</p> : null}
            {calloutSubtitle ? <p className="truncate text-[12px] leading-tight" style={{ color: `color-mix(in srgb, ${calloutText} 80%, transparent)` }}>{calloutSubtitle}</p> : null}
          </button>
        </div>
      ) : null}
      {!launcherOnlyWhenClosed && !open && pendingPreview ? (
        <div
          role="status"
          aria-live="polite"
          className={cn("chat-preview-bubble flex w-80 items-start gap-2 rounded-2xl border p-2.5 shadow-[0_8px_20px_-6px_rgba(35,28,74,0.22)]", contained ? "relative w-full max-w-none mb-3" : "absolute max-w-[calc(100vw-110px)]", !contained && (positionIsTop ? "top-full mt-3" : "bottom-full mb-3"), !contained && (positionIsLeft ? "left-0" : "right-0"))}
          style={{ borderRadius: radius(16), minWidth: "260px", maxWidth: "min(320px, calc(100vw - 110px))", backgroundColor: assistantBg, color: textColor, borderColor: `color-mix(in srgb, ${textColor} 14%, transparent)` }}
        >
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-xl"
            style={primaryGradient.enabled ? { background: primaryGradientCss } : { backgroundColor: primaryColor }}
            aria-hidden
          >
            {primaryGradient.enabled ? (
              <span className="flex size-5 items-center justify-center rounded-md" style={{ backgroundColor: theme.chatBackground, color: readableTextColor(theme.chatBackground, textColor, 3), boxShadow: `0 0 0 1px color-mix(in srgb, ${textColor} 18%, transparent)` }}>
                <Sparkles className="size-3.5" />
              </span>
            ) : <Sparkles className="size-4 text-white" />}
          </span>
          <button
            type="button"
            onClick={() => { setOpen(true); setPendingPreview(null) }}
            className="min-w-0 flex-1 text-left"
          >
            <p className="truncate text-[10px] font-bold uppercase tracking-wide" style={{ color: `color-mix(in srgb, ${textColor} 55%, transparent)` }}>{headerTitle}</p>
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed">{pendingPreview}</p>
            <p className={cn("mt-2 inline-flex items-center gap-1 text-[10px] font-semibold", primaryGradient.enabled && "widget-primary-gradient-text")} style={previewPrimaryTextStyle}>
              <PrimaryGradientIcon icon={Bell} className="size-2.5" color={primaryColor} gradient={primaryIconGradientOnAssistant} />Nova resposta
            </p>
          </button>
          <button
            type="button"
            aria-label="Dispensar notificação"
            onClick={(event) => { event.stopPropagation(); setPendingPreview(null) }}
            className="flex size-6 shrink-0 items-center justify-center rounded-lg transition-colors"
            style={{ color: `color-mix(in srgb, ${textColor} 55%, transparent)` }}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ) : null}
            {renderOpen ? (
        <MessageScrollerProvider>
          <Card
            className={cn(
              "flex flex-col gap-0 overflow-hidden border-0 p-0",
              cardMotionClass,
            )}
            style={widgetCardStyle}
          >
            {/* ── Header ── */}
            <div data-widget-motion-layer className="flex shrink-0 items-center" style={headerStyle}>
              <div
                className="flex min-w-0 items-center"
                style={{
                  gap: 14.88,
                  width: isFigmaDefaultGeometry ? 252.52 : undefined,
                  flex: isFigmaDefaultGeometry ? "0 0 252.52px" : "1 1 auto",
                }}
              >
                <div className="relative flex size-[44.64px] shrink-0 items-center justify-center overflow-hidden" style={{ ...avatarFrameStyle, color: headerText, borderRadius: settings.avatarUrl ? `${avatarRadius}%` : radius(10) }}>
                  <div className="flex size-full items-center justify-center overflow-hidden" style={{ background: avatarTintBackground, borderRadius: settings.avatarUrl ? `${avatarRadius}%` : radius(10) }}>
                    {settings.avatarUrl ? <img src={settings.avatarUrl} alt="" className="size-full object-cover object-top" /> : <AvatarIcon avatar={settings.avatar} className="size-[17px]" gradient={headerIconGradient} />}
                  </div>
                </div>
                <div className="min-w-0 flex-1" style={isFigmaDefaultGeometry ? { width: 193, flex: "0 0 193px" } : undefined}>
                  <p className={cn("truncate text-[14px] font-bold", isFigmaDefaultGeometry ? "leading-normal" : "leading-tight", primaryGradient.enabled && "widget-primary-gradient-text")} style={headerTitleStyle} title={headerTitle}>
                    {headerTitle}
                  </p>
                  {settings.chatSubtitle && (
                    <p
                      className={cn("flex items-center truncate", isFigmaDefaultGeometry ? "mt-[0.744px] leading-normal" : "mt-1 gap-1.5 text-[12px] leading-tight")}
                      style={{
                        color: subtitleColor,
                        fontFamily: "Nunito, DM Sans, sans-serif",
                        fontSize: isFigmaDefaultGeometry ? 12.648 : 12,
                        fontWeight: 500,
                        ...(isFigmaDefaultGeometry ? { gap: 3.72 } : {}),
                      }}
                    >
                      <span className="size-[4.464px] shrink-0 rounded-full" style={{ backgroundColor: subtitleColor }} />{settings.chatSubtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="relative flex shrink-0 items-center justify-center" style={{ width: isFigmaDefaultGeometry ? 17.856 : 32, height: isFigmaDefaultGeometry ? 17.856 : 32 }}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Fechar"
                  className={cn("transition-colors", isFigmaDefaultGeometry ? "absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2" : "size-8")}
                  style={{ color: headerActionText, borderRadius: radius(9) }}
                  onMouseEnter={(event) => { (event.currentTarget as HTMLButtonElement).style.backgroundColor = `color-mix(in srgb, ${headerActionText} 13%, transparent)` }}
                  onMouseLeave={(event) => { (event.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent" }}
                  onClick={() => setOpen(false)}
                >
                  <X className={isFigmaDefaultGeometry ? "size-[17.856px]" : "size-[18px]"} />
                </Button>
              </div>
            </div>

            {/* ── Body ── */}
            <CardContent data-widget-motion-layer className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              {showEmptyState ? (
                <div
                  className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[13px] pt-4 pb-5"
                  style={{ backgroundColor: "transparent", gap: isFigmaDefaultGeometry ? 23.808 : 0 }}
                >
                  <div className="flex w-full flex-col text-center" style={isFigmaDefaultGeometry ? { gap: 4.464 } : undefined}>
                    <h2
                      className={cn("w-full text-center font-bold", isFigmaDefaultGeometry ? "leading-normal" : "leading-[1.3]")}
                      style={{ color: textColor, fontSize: welcomeHeadingFontSize, fontWeight: 700, lineHeight: isFigmaDefaultGeometry ? "normal" : 1.3 }}
                    >
                      {settings.welcomeMessage}
                    </h2>
                    <p
                      className={cn("w-full text-center", isFigmaDefaultGeometry ? "leading-[1.5]" : "mt-[4.46px] leading-[1.42]")}
                      style={{
                        color: "#9A9A9A",
                        fontFamily: isFigmaDefaultGeometry ? "DM Sans, sans-serif" : "Nunito, DM Sans, sans-serif",
                        fontSize: welcomeHelperFontSize,
                        fontWeight: 500,
                        lineHeight: isFigmaDefaultGeometry ? 1.5 : 1.42,
                      }}
                    >
                      Como posso te ajudar?
                    </p>
                  </div>

                  {settings.suggestedQuestions.length > 0 && (
                    <>
                      <div aria-hidden="true" className="relative h-0 w-full shrink-0" style={!isFigmaDefaultGeometry ? { marginTop: 23.8 } : undefined}>
                        <span className="absolute inset-x-0 top-0 border-t" style={{ borderColor: "#E6E6E6", borderTopWidth: "0.744px" }} />
                      </div>
                      <div
                        aria-label="Perguntas sugeridas"
                        className="flex w-full min-h-0 flex-col overflow-y-auto"
                        style={{ gap: 7.44, marginTop: isFigmaDefaultGeometry ? undefined : 23.8 }}
                      >
                        {settings.suggestedQuestions.map((question) => (
                          <button
                            key={question}
                            type="button"
                            onClick={() => void submitMessage(question)}
                            className={cn("flex min-h-[52px] w-full items-center text-left transition-colors", isFigmaDefaultGeometry && "shrink-0")}
                            style={{
                              backgroundColor: "#F9F9F9",
                              color: textColor,
                              border: "0.744px solid #ECECEC",
                              borderRadius: suggestionRadius,
                              padding: "8px 8.928px 8px 11.904px",
                              fontSize: messageFontSize,
                              fontWeight: 400,
                              lineHeight: isFigmaDefaultGeometry ? "normal" : 1.25,
                              ...(isFigmaDefaultGeometry ? { height: 52 } : {}),
                            }}
                          >
                            <span className="flex-1">{question}</span>
                            <PrimaryGradientIcon icon={ChevronRight} aria-hidden="true" className="size-[17.856px] shrink-0" color={isFigmaDefaultPrimaryGradient ? theme.primaryColor : primaryColor} gradient={isFigmaDefaultPrimaryGradient ? undefined : primaryIconGradientOnSuggestion} />
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <MessageScroller>
                  <MessageScrollerViewport className="chat-scrollbar">
                    <MessageScrollerContent
                      aria-busy={sending}
                      className="gap-5 p-5"
                    >
                      {messages.map((message, index) =>
                        message.role === "user" ? (
                          /* User: right-aligned bubble */
                          <div key={message.id} className="flex flex-col items-end gap-1.5">
                            <div
                              className="max-w-[64%] px-3 py-2.5 leading-5"
                              style={{ ...(primaryGradient.enabled ? { background: primaryGradientCss } : { backgroundColor: theme.userMessageColor }), color: userText, fontSize: messageFontSize, fontWeight: messageFontWeight, opacity: message.failedReason ? 0.7 : 1, borderRadius: `${radius(13)}px ${radius(13)}px ${radius(4)}px ${radius(13)}px` }}
                            >
                              {message.content}
                            </div>
                            {message.failedReason ? (
                              <div role="alert" className="flex max-w-[85%] flex-wrap items-center justify-end gap-x-2 gap-y-1 text-right text-[12px] leading-4" style={{ color: shellDark ? "#FDA29B" : "#B42318" }}>
                                <span>{message.failedReason}</span>
                                {index === messages.length - 1 ? (
                                  <button
                                    type="button"
                                    className="font-semibold underline underline-offset-2 disabled:opacity-50"
                                    disabled={sending || unavailable}
                                    onClick={() => void submitMessage(message.content, message.id)}
                                  >
                                    Tentar novamente
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          /* Assistant: avatar and message bubble follow the approved chat pattern. */
                          <div key={message.id} className="flex items-end gap-2">
                            <div className="flex size-7 shrink-0 items-center justify-center overflow-hidden" style={{ ...(avatarGradient ? { background: avatarTintBackground } : { backgroundColor: avatarTintBackground }), color: avatarColor, borderRadius: `${avatarRadius}%` }}>
                              {settings.avatarUrl ? <img src={settings.avatarUrl} alt="" className="size-full object-cover object-top" /> : <AvatarIcon avatar={settings.avatar} className="size-3.5" gradient={avatarGradient} />}
                            </div>
                            <div className="max-w-[min(282px,calc(100%-36px))] border px-3 py-2.5 leading-5" style={{ color: textColor, backgroundColor: assistantBg, borderColor: `color-mix(in srgb, ${textColor} 10%, transparent)`, fontSize: messageFontSize, fontWeight: messageFontWeight, borderRadius: `${radius(13)}px ${radius(13)}px ${radius(13)}px ${radius(4)}px` }}>
                              {message.authorType === "genesys_agent" ? <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-60">Atendente</p> : null}
                              {message.authorType === "genesys_automation" ? <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-60">Atualização do atendimento</p> : null}
                              {message.authorType === "genesys_automation" && parseGenesysQueueUpdate(message.content) ? (
                                <div className="space-y-1">
                                  <p className="font-semibold">Aguarde para ser atendido.</p>
                                  {parseGenesysQueueUpdate(message.content)?.position ? <p>Posição na fila: {parseGenesysQueueUpdate(message.content)?.position}</p> : null}
                                  {parseGenesysQueueUpdate(message.content)?.wait ? <p>Tempo estimado: {parseGenesysQueueUpdate(message.content)?.wait}</p> : null}
                                </div>
                              ) : (
                                <MarkdownMessage content={message.content} onOutboundClick={(href) => trackWidgetEvent(publicKey, sessionToken, sessionKey, "outbound_click", conversationId, href)} />
                              )}
                              {sending && index === messages.length - 1 && message.content.length > 0 ? <span className="chat-stream-cursor" aria-hidden /> : null}
                            </div>
                          </div>
                        ),
                      )}

                      {showThinkingDots && (
                        <div role="status" className="flex items-center gap-1 py-1">
                          <span className="sr-only">Preparando resposta...</span>
                          <span className="size-1.5 animate-bounce rounded-full [animation-delay:-.2s]" style={{ backgroundColor: `color-mix(in srgb, ${textColor} 40%, transparent)` }} />
                          <span className="size-1.5 animate-bounce rounded-full [animation-delay:-.1s]" style={{ backgroundColor: `color-mix(in srgb, ${textColor} 40%, transparent)` }} />
                          <span className="size-1.5 animate-bounce rounded-full" style={{ backgroundColor: `color-mix(in srgb, ${textColor} 40%, transparent)` }} />
                        </div>
                      )}
                    </MessageScrollerContent>
                  </MessageScrollerViewport>
                  <MessageScrollerButton />
                </MessageScroller>
              )}
              {genesysQueueStatus && genesysHandoffState === "recovering" ? (
                <div
                  role="status"
                  className={cn("flex shrink-0 items-center gap-2 border-t px-5 py-2 text-[11px]", isCompact && "px-3")}
                  style={{ color: `color-mix(in srgb, ${textColor} 72%, transparent)`, borderColor: `color-mix(in srgb, ${textColor} 12%, transparent)` }}
                >
                  <span className="size-1.5 shrink-0 animate-pulse rounded-full" style={primaryGradient.enabled ? { background: primaryGradientCss } : { backgroundColor: primaryColor }} aria-hidden="true" />
                  <span className="min-w-0 truncate">
                    Na fila <strong style={{ color: textColor }}>{genesysQueueStatus.queueName}</strong>
                    {typeof genesysQueueStatus.estimatedWaitTimeSeconds === "number"
                      ? ` · espera estimada: ${genesysQueueStatus.estimatedWaitTimeSeconds < 60 ? "menos de 1 min" : `cerca de ${Math.ceil(genesysQueueStatus.estimatedWaitTimeSeconds / 60)} min`}`
                      : " · aguardando atendimento"}
                  </span>
                </div>
              ) : null}
            </CardContent>

            {/* ── Footer / Prompt input ── */}
            <CardFooter data-widget-motion-layer className={cn("flex shrink-0 flex-col gap-2 px-[13px]", showEmptyState ? "border-t-0 pt-0 pb-[27px]" : cn("border-t pt-3", isCompact ? "pb-2.5" : "pb-3"))} style={{ backgroundColor: theme.chatBackground, borderColor: "#E6E6E6" }}>
              {!showEmptyState && !genesysHandoffState ? (
                <button
                  type="button"
                  onClick={returnToMenu}
                  disabled={sending}
                  className="flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50"
                  style={{ color: textColor, borderColor: `color-mix(in srgb, ${textColor} 14%, transparent)` }}
                >
                  <PrimaryGradientIcon icon={ArrowLeft} aria-hidden="true" className="size-3.5" color={primaryColor} gradient={primaryIconGradient} />
                  Voltar ao menu
                </button>
              ) : null}
              {settings.quickActions.length > 0 && (
                <div className="flex w-full flex-wrap gap-1.5">
                  {settings.quickActions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      onClick={() => void submitMessage(action.message)}
                      disabled={unavailable}
                      className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors"
                      style={{
                        backgroundColor: assistantBg,
                        color: textColor,
                        borderColor: `color-mix(in srgb, ${textColor} 14%, transparent)`,
                      }}
                    >
                      <AvatarIcon avatar={action.icon} className="size-3.5" style={{ color: primaryColor }} gradient={primaryIconGradient} />
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
              <form onSubmit={send} className="w-full">
                <InputGroup style={{
                  backgroundColor: theme.chatBackground,
                  color: textColor,
                  borderColor: "transparent",
                  borderRadius: inputRadius,
                  boxShadow: inputShadow,
                  minHeight: 41,
                }} className="relative">
                  {/* Textarea */}
                  <Textarea
                    ref={textareaRef}
                    rows={1}
                    aria-label="Mensagem"
                    className={cn("max-h-[120px] min-h-[39px] resize-none border-0 bg-transparent py-2 pr-12 shadow-none focus-visible:ring-0 [&::placeholder]:text-[var(--widget-placeholder-color)] [&::placeholder]:opacity-100", isCompact ? "pl-3 text-[13px]" : "pl-4 text-[14px]", placeholderGradientCss && "widget-primary-gradient-placeholder")}
                    style={{ caretColor: isFigmaDefaultPrimaryGradient ? theme.primaryColor : primaryColor, color: textColor, borderRadius: inputRadius, "--widget-placeholder-color": isFigmaDefaultPrimaryGradient ? theme.primaryColor : primaryColor, ...(placeholderGradientCss ? { "--widget-primary-gradient-text": placeholderGradientCss } : {}) } as CSSProperties}
                    placeholder={restoringSession
                      ? "Restaurando conversa..."
                      : unavailable
                      ? "Atendimento indisponível no momento"
                      : genesysHandoffState === "active"
                        ? "Escreva para o atendente..."
                        : genesysHandoffState
                          ? "Aguardando retorno da equipe..."
                          : settings.placeholder}
                    value={input}
                    disabled={unavailable || restoringSession}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault()
                        void submitMessage(input.trim())
                      }
                    }}
                  />

                  <InputGroupButton
                    type="submit"
                    size="icon-sm"
                    variant="default"
                    disabled={!canSend}
                    aria-label="Enviar mensagem"
                  className="absolute right-2 top-1/2 rounded-full -translate-y-1/2 hover:bg-transparent"
                    style={{ ...submitStyle, width: 26, height: 26 }}
                    onClick={send as unknown as React.MouseEventHandler<HTMLButtonElement>}
                  >
                    {sending ? <PrimaryGradientIcon icon={LoaderCircle} className="size-4 animate-spin" color={submitStyle.color as string} gradient={settings.submitColorStyle !== "neutral" ? primaryIconGradient : undefined} /> : <span aria-hidden="true" className={isFigmaDefaultGeometry ? "size-[26px]" : "size-6"} style={{ display: "block", ...(settings.submitColorStyle !== "neutral" && primaryGradient.enabled ? { backgroundImage: primaryIconGradientCss } : { backgroundColor: submitStyle.color as string }), maskImage: "url('/images/mil-play-send-icon.svg')", WebkitMaskImage: "url('/images/mil-play-send-icon.svg')", maskPosition: "center", WebkitMaskPosition: "center", maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskSize: "contain", WebkitMaskSize: "contain" }} />}
                    <span className="sr-only">Enviar</span>
                  </InputGroupButton>
                </InputGroup>
              </form>

              {(settings.showBranding || settings.showPoweredBy) && (
                <p className="text-center text-[9px] font-medium" style={{ color: `color-mix(in srgb, ${textColor} 55%, transparent)` }}>
                  {settings.showBranding && <span className="font-bold">Powered by BubbleDesk</span>}
                  {settings.showBranding && settings.showPoweredBy && <span className="mx-1">·</span>}
                  {settings.showPoweredBy && <a href="https://usabit.com.br" target="_blank" rel="noopener noreferrer" className="hover:underline">Usabit</a>}
                </p>
              )}
            </CardFooter>
          </Card>
        </MessageScrollerProvider>
      ) : null}
      <Button
        type="button"
        size="icon"
        className={cn(
          "widget-launcher shrink-0 overflow-hidden",
          settings.launcherStyle === "soft" || settings.launcherStyle === "outline" ? "border" : "border-0",
          expandedLayout && "mt-[10px]",
          launcherMotionClass,
        )}
        style={{
          ...renderedLauncherStyle,
          width: settings.launcherSize,
          height: settings.launcherSize,
          minWidth: settings.launcherSize,
          minHeight: settings.launcherSize,
          flex: "0 0 auto",
          aspectRatio: "1 / 1",
          borderRadius: settings.launcherRadius,
        }}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Fechar chat" : "Abrir chat"}
        aria-expanded={open}
      >
        {open ? (
          <X aria-hidden="true" className="size-[40%]" style={{ color: launcherStyle.color }} />
        ) : launcherCustom ? (
          <img src={settings.launcherIcon} alt="" className="widget-launcher-icon size-full" style={{ objectFit: "cover", borderRadius: settings.launcherRadius }} />
        ) : (
          <AvatarIcon avatar={settings.launcherIcon} className="widget-launcher-icon" style={{ color: launcherStyle.color, width: "53.333%", height: "53.333%" }} />
        )}
      </Button>
    </div>
  )
}
